import { ShopifyStore, getBaseStore } from 'lib/commerce/shopify/shopifyHelper';

/**
 * Configuration for a store that inherits from a base store.
 * Only the price needs to be specified, other fields are inherited from the base store.
 */
export type AliasedStoreConfig = {
  price: number;
};

/**
 * Full configuration for a base store.
 * This is the complete product configuration including all variant and checkout details.
 */
export type BaseStoreConfig<T = any> = T & {
  price: number;
};

/**
 * Product configuration input where aliased stores can provide just their price.
 * Base stores (IN, AE, US, EU, ROW) provide full configuration.
 * Aliased stores (GB, AU, CA, SA, MX, ZA, JP) can provide just price overrides.
 */
export type StoreConfigInput<T> = {
  [K in ShopifyStore]?: K extends
    | ShopifyStore.GB
    | ShopifyStore.AU
    | ShopifyStore.CA
    | ShopifyStore.SA
    | ShopifyStore.MX
    | ShopifyStore.ZA
    | ShopifyStore.JP
    ? AliasedStoreConfig | BaseStoreConfig<T>
    : BaseStoreConfig<T>;
};

/**
 * Builds a complete store configuration by expanding aliased stores.
 *
 * Aliased stores (GB, AU, CA) inherit variant IDs and checkout URLs from their base stores,
 * but can have different prices.
 *
 * Example:
 * ```typescript
 * const config = buildStoreConfig({
 *   [ShopifyStore.EU]: { price: 42, variantId: 'eu-variant', checkoutUrl: 'eu-url' },
 *   [ShopifyStore.GB]: { price: 39 }, // inherits variantId and checkoutUrl from EU
 *   [ShopifyStore.AU]: { price: 69 }, // inherits variantId and checkoutUrl from EU
 *   [ShopifyStore.US]: { price: 45, variantId: 'us-variant', checkoutUrl: 'us-url' },
 *   [ShopifyStore.CA]: { price: 69 }, // inherits variantId and checkoutUrl from US
 * });
 * ```
 *
 * @param input - Partial store configuration with base stores fully defined and aliased stores optionally providing price overrides
 * @returns Complete store configuration with all stores fully expanded
 */
export function buildStoreConfig<T extends Record<string, any>>(
  input: StoreConfigInput<T>,
): Record<ShopifyStore, BaseStoreConfig<T>> {
  const result = {} as Record<ShopifyStore, BaseStoreConfig<T>>;

  // Process each store
  for (const store of Object.values(ShopifyStore)) {
    const storeConfig = input[store];
    const baseStore = getBaseStore(store);

    if (storeConfig) {
      // If config is provided for this store, use it directly
      result[store] = storeConfig as BaseStoreConfig<T>;
    } else if (baseStore !== store) {
      // This is an aliased store without explicit config
      // Inherit from base store if it exists
      const baseConfig = input[baseStore];
      if (baseConfig) {
        result[store] = { ...baseConfig } as BaseStoreConfig<T>;
      }
    }
  }

  // Second pass: Handle stores that provided only price (aliased stores with partial config)
  for (const store of Object.values(ShopifyStore)) {
    const storeConfig = input[store];
    const baseStore = getBaseStore(store);

    if (storeConfig && baseStore !== store) {
      // Check if this is a partial config (only has price)
      const configKeys = Object.keys(storeConfig);
      const isPriceOnly = configKeys.length === 1 && configKeys[0] === 'price';

      if (isPriceOnly) {
        // Inherit everything from base store except price
        const baseConfig = input[baseStore];
        if (baseConfig) {
          result[store] = {
            ...baseConfig,
            price: storeConfig.price,
          } as BaseStoreConfig<T>;
        }
      }
    }
  }

  return result;
}

/**
 * Helper to build nested store configurations for products with variants (like ring sizes).
 * This version handles the structure: Store -> Variant -> Config
 *
 * Example:
 * ```typescript
 * const accessoryConfig = buildAccessoryStoreConfig({
 *   [ShopifyStore.EU]: {
 *     price: 42,
 *     variants: {
 *       [RingSizes.FIVE]: { variantId: 'eu-5', shopifyVariantId: 'gid://...', checkoutUrl: '...' },
 *       [RingSizes.SIX]: { variantId: 'eu-6', shopifyVariantId: 'gid://...', checkoutUrl: '...' },
 *     }
 *   },
 *   [ShopifyStore.GB]: { price: 39 }, // inherits variants from EU
 * });
 * ```
 */
export type VariantConfig = {
  variantId: string;
  shopifyVariantId: string;
  checkoutUrl: string;
};

export type StoreWithVariantsInput<K extends string | number> = {
  price: number;
  variants?: Record<K, VariantConfig>;
};

export type StoreWithVariants<K extends string | number> = {
  price: number;
} & Record<K, VariantConfig>;

export function buildAccessoryStoreConfig<K extends string | number>(input: {
  [S in ShopifyStore]?: StoreWithVariantsInput<K>;
}): Record<ShopifyStore, StoreWithVariants<K>> {
  const result = {} as Record<ShopifyStore, StoreWithVariants<K>>;

  // First pass: set up base configs
  for (const store of Object.values(ShopifyStore)) {
    const storeConfig = input[store];
    const baseStore = getBaseStore(store);

    if (storeConfig && storeConfig.variants) {
      // This store has full config
      result[store] = {
        price: storeConfig.price,
        ...storeConfig.variants,
      } as StoreWithVariants<K>;
    } else if (storeConfig && !storeConfig.variants) {
      // This store only has price, inherit variants from base
      const baseConfig = input[baseStore];
      if (baseConfig && baseConfig.variants) {
        result[store] = {
          price: storeConfig.price,
          ...baseConfig.variants,
        } as StoreWithVariants<K>;
      } else {
        result[store] = { price: storeConfig.price } as StoreWithVariants<K>;
      }
    } else if (baseStore !== store) {
      // No config, inherit everything from base
      const baseConfig = input[baseStore];
      if (baseConfig && baseConfig.variants) {
        result[store] = {
          price: baseConfig.price,
          ...baseConfig.variants,
        } as StoreWithVariants<K>;
      }
    }
  }

  return result;
}

/**
 * Helper for ring product configurations where store mappings are properties within the config.
 * Used for structures like: Color -> Size -> RingType -> { variantId: Record<Store, string>, checkoutUrl: Record<Store, string> }
 *
 * This function processes store mappings that are nested inside product configs, expanding
 * aliased stores (GB, AU, CA) to inherit from their base stores.
 */
export function expandStoreAliases<T extends Record<string, any>>(
  config: T,
): T {
  const result = { ...config };

  // Find all properties that are store mappings (Record<ShopifyStore, any>)
  for (const key in config) {
    const value = config[key];

    // Check if this property is a store mapping object
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Check if it has ShopifyStore keys
      const hasStoreKeys = Object.keys(value).some((k) =>
        Object.values(ShopifyStore).includes(k as ShopifyStore),
      );

      if (hasStoreKeys) {
        // This is a store mapping, expand it
        const expandedMapping: Record<string, any> = { ...value };

        // Fill in aliased stores
        for (const store of Object.values(ShopifyStore)) {
          if (!expandedMapping[store]) {
            const baseStore = getBaseStore(store);
            if (baseStore !== store && expandedMapping[baseStore]) {
              expandedMapping[store] = expandedMapping[baseStore];
            }
          }
        }

        result[key] = expandedMapping as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
}

/**
 * Deep version of expandStoreAliases that recursively processes nested objects.
 * Use this for complex nested structures like ring product maps.
 */
export function deepExpandStoreAliases<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepExpandStoreAliases(item)) as any;
  }

  // Check if this object itself is a store mapping
  const hasStoreKeys = Object.keys(obj).some((k) =>
    Object.values(ShopifyStore).includes(k as ShopifyStore),
  );

  if (hasStoreKeys) {
    // This is a store mapping, expand it
    const expandedMapping: Record<string, any> = { ...obj };

    for (const store of Object.values(ShopifyStore)) {
      if (!expandedMapping[store]) {
        const baseStore = getBaseStore(store);
        if (baseStore !== store && expandedMapping[baseStore]) {
          expandedMapping[store] = expandedMapping[baseStore];
        }
      }
    }

    return expandedMapping as T;
  }

  // Recursively process nested objects
  const result: Record<string, any> = {};
  for (const key in obj) {
    result[key] = deepExpandStoreAliases((obj as any)[key]);
  }
  return result as T;
}

/**
 * Merch-specific types
 */
export type MerchSizeConfig = {
  variantId: string;
  checkoutUrl: string;
};

export type MerchStoreConfig<K extends string | number> = {
  price: number;
  sizes: Record<K, MerchSizeConfig>;
};

export type MerchStoreConfigInput<K extends string | number> = {
  price: number;
  sizes?: Record<K, MerchSizeConfig>;
};

/**
 * Helper for merch product configurations (t-shirts, hoodies, etc.)
 * Structure: Store -> { price, sizes: { SizeX: { variantId, checkoutUrl } } }
 */
export function buildMerchStoreConfig<K extends string | number>(input: {
  [S in ShopifyStore]?: MerchStoreConfigInput<K>;
}): Record<ShopifyStore, MerchStoreConfig<K>> {
  const result = {} as Record<ShopifyStore, MerchStoreConfig<K>>;

  // Process each store
  for (const store of Object.values(ShopifyStore)) {
    const storeConfig = input[store];
    const baseStore = getBaseStore(store);

    if (storeConfig && storeConfig.sizes) {
      // This store has full config
      result[store] = storeConfig as MerchStoreConfig<K>;
    } else if (storeConfig && !storeConfig.sizes) {
      // This store only has price, inherit sizes from base
      const baseConfig = input[baseStore];
      if (baseConfig && baseConfig.sizes) {
        result[store] = {
          price: storeConfig.price,
          sizes: baseConfig.sizes,
        };
      }
    } else if (baseStore !== store) {
      // No config, inherit everything from base
      const baseConfig = input[baseStore];
      if (baseConfig && baseConfig.sizes) {
        result[store] = {
          price: baseConfig.price,
          sizes: baseConfig.sizes,
        };
      }
    }
  }

  return result;
}
